import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../i18n/LanguageProvider";

const STORAGE_KEY = "zelorq_cookie_consent_v1";

function updateGtagConsent(prefs) {
  if (typeof window === "undefined" || typeof window.gtag !== "function")
    return;
  window.gtag("consent", "update", {
    ad_storage: prefs.marketing ? "granted" : "denied",
    ad_user_data: prefs.marketing ? "granted" : "denied",
    ad_personalization: prefs.marketing ? "granted" : "denied",
    analytics_storage: prefs.analytics ? "granted" : "denied",
  });
}

export default function CookieBanner() {
  const { lang, t } = useI18n();
  const [visible, setVisible] = useState(false);
  const [customizing, setCustomizing] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false, marketing: false });

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        setVisible(true);
      } else {
        const saved = JSON.parse(raw);
        updateGtagConsent(saved);
      }
    } catch {
      setVisible(true);
    }
  }, []);

  const save = (choice) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(choice));
    } catch {}
    updateGtagConsent(choice);
    setVisible(false);
    setCustomizing(false);
  };

  const acceptAll = () => save({ analytics: true, marketing: true });
  const rejectAll = () => save({ analytics: false, marketing: false });
  const savePrefs = () => save(prefs);

  if (!visible) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[60]"
      role="dialog"
      aria-label={t("cookies.banner_title")}
      data-testid="cookie-banner"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 pb-4">
        <div className="bg-white border border-[#E2E8F0] shadow-xl rounded-sm">
          <div className="p-5 lg:p-7 grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-5 lg:gap-8 items-start">
            <div>
              <h2 className="font-heading text-lg font-semibold text-slate-900 mb-2">
                {t("cookies.banner_title")}
              </h2>
              <p className="text-sm text-slate-600 leading-relaxed">
                {t("cookies.banner_body")}{" "}
                <Link
                  to={`/${lang}/cookie-policy`}
                  className="text-[#C85A32] font-medium underline underline-offset-2"
                  data-testid="cookie-banner-read-more"
                >
                  {t("cookies.read_more")}
                </Link>
                .
              </p>

              {customizing && (
                <div
                  className="mt-5 border-t border-[#E2E8F0] pt-4 space-y-3 text-sm"
                  data-testid="cookie-customize-panel"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-slate-700">
                      {t("cookies.necessary")}
                    </span>
                    <input
                      type="checkbox"
                      checked
                      disabled
                      className="w-4 h-4 accent-[#0F2537]"
                    />
                  </div>
                  <label className="flex items-center justify-between cursor-pointer">
                    <span className="text-slate-700">
                      {t("cookies.analytics")}
                    </span>
                    <input
                      type="checkbox"
                      checked={prefs.analytics}
                      onChange={(e) =>
                        setPrefs((p) => ({ ...p, analytics: e.target.checked }))
                      }
                      className="w-4 h-4 accent-[#0F2537]"
                      data-testid="cookie-toggle-analytics"
                    />
                  </label>
                  <label className="flex items-center justify-between cursor-pointer">
                    <span className="text-slate-700">
                      {t("cookies.marketing")}
                    </span>
                    <input
                      type="checkbox"
                      checked={prefs.marketing}
                      onChange={(e) =>
                        setPrefs((p) => ({ ...p, marketing: e.target.checked }))
                      }
                      className="w-4 h-4 accent-[#0F2537]"
                      data-testid="cookie-toggle-marketing"
                    />
                  </label>
                </div>
              )}
            </div>

            <div className="flex flex-col gap-2 lg:min-w-[220px]">
              {!customizing ? (
                <>
                  <button
                    type="button"
                    onClick={acceptAll}
                    className="w-full px-4 py-2.5 bg-[#0F2537] hover:bg-[#0a1c2b] text-white text-sm font-semibold rounded-sm"
                    data-testid="cookie-accept-all"
                  >
                    {t("cookies.accept_all")}
                  </button>
                  <button
                    type="button"
                    onClick={rejectAll}
                    className="w-full px-4 py-2.5 border border-[#0F2537] text-[#0F2537] hover:bg-slate-50 text-sm font-semibold rounded-sm"
                    data-testid="cookie-reject-all"
                  >
                    {t("cookies.reject_all")}
                  </button>
                  <button
                    type="button"
                    onClick={() => setCustomizing(true)}
                    className="w-full px-4 py-2.5 text-sm font-medium text-slate-600 hover:text-slate-900"
                    data-testid="cookie-customize"
                  >
                    {t("cookies.customize")}
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={savePrefs}
                  className="w-full px-4 py-2.5 bg-[#C85A32] hover:bg-[#b04d28] text-white text-sm font-semibold rounded-sm"
                  data-testid="cookie-save"
                >
                  {t("cookies.save")}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
